import { PayloadAction } from '@reduxjs/toolkit'
import { call, takeLatest, put } from 'redux-saga/effects'
import axios from 'shared/utils/axios'
import { exportSurvey, exportAllSurvey } from './reducer'
import { exportJsonToCsv, formatISOToDate } from 'shared/utils/tools'
import queryString from 'qs'
import salaryOptions from 'shared/constants/salaryOptions'
import { showSuccess, showError } from '../app/reducer'
import { SurveyFormSchema } from 'pages/Survey/model/survey'
import { PaginatedList } from 'shared/types'
import range from 'lodash/range'
import groupBy from 'lodash/groupBy'
import values from 'lodash/values'
import { OTHER_GENDER_OPTION, RACE_SELF_DESCRIBE_OPTION } from 'shared/constants/user'
import pick from 'lodash/pick'
import day from 'dayjs'

function* ExportSurvey(action: PayloadAction<{ id: string; name: string }>) {
  try {
    let { participants, questions }: any = yield call(axios.request, {
      url: `/survey/${action.payload.id}/report`,
    })

    const answer_keys = questions.map(
      ({ item_code, question_text, question_id }: any) => `${question_id.trim()}: Answer`,
    )
    const values_keys = questions.map(
      ({ item_code, question_text, question_id }: any) => `${question_id.trim()}: ${question_text?.replace(/,/g, '')}`,
    )
    const fields = [
      'Member ID',
      'User Group',
      'Date Completed',
      'Survey Name',
      'First name',
      'Last name',
      'Status',
      'Age',
      'Gender',
      'Race',
      'Income',
      'Medical Consideration',
      ...answer_keys,
      ...values_keys,
    ]

    if (participants.length === 0) {
      yield put(
        showError({
          message: 'No Records Found',
          subText: `There are no participants that have answered this survey.`,
          showSubText: true,
        }),
      )
      return false
    }

    participants = participants.map((each: any) => {
      // group the answer if same item_code
      each.answers = values(groupBy(each.answers, 'item_code')).reduce(
        (acc: any[], curr: any) =>
          acc.concat(
            curr.reduce(
              (answers: any, answer: any) => ({
                ...answer,
                answer_label: `${answers?.answer_label ? answers?.answer_label + '\n' : ''} ${answer?.answer_label ||
                  ''}`,
                answer_value: `${answers?.answer_value ? answers?.answer_value + '\n' : ''} ${answer?.answer_value ||
                  ''}`,
              }),
              {},
            ),
          ),
        [],
      )

      let answers = each.answers.reduce((acc: any, curr: any) => {
        const question = questions.find(({ item_code, question_id }: any) => item_code === curr.item_code)

        if (!question) return acc

        return {
          ...acc,
          [`${question.question_id.trim()}: Answer`]: (question.type === 'textbox'
            ? 'N/A'
            : curr?.answer_label || 'N/A'
          )?.trim(),
          [`${question.question_id.trim()}:  ${question.question_text?.replace(/,/g, '')}`]: (question.type ===
          'textbox'
            ? 'N/A'
            : curr?.answer_value || 'N/A'
          )?.trim(),
        }
      }, {})

      const sort = (a: any, b: any) => a?.[0]?.localeCompare(b?.[0], undefined, { numeric: true, sensitivity: 'base' })
      answers = Object.entries(answers)
        .sort(sort)
        .reduce((object, [key, value]) => ({ ...object, [key]: value }), {})

      let race = each.race

      if (each.race) {
        if (typeof each.race === 'string') {
          race = RACE_SELF_DESCRIBE_OPTION
          if (each.race !== RACE_SELF_DESCRIBE_OPTION) {
            race = each.race
          }
        } else {
          if (each.race.includes(RACE_SELF_DESCRIBE_OPTION)) {
            race = each.specific_race
          } else {
            race = each.race.join(' ')
          }
        }
      }

      let salary_range_arr = salaryOptions.filter(salary_option => {
        return salary_option.value === each.household_income
      })

      let salary_range_label = each.household_income
      if (salary_range_arr.length) salary_range_label = salary_range_arr[0].label

      return {
        'Member ID': each?.member_id || 'N/A',
        'User Group': each?.role_name || 'N/A',
        'Date Completed': each?.completed_date ? day(each?.completed_date).format('MM/DD/YYYY') : 'N/A',
        'Survey Name': each?.survey || 'N/A',
        'First name': each?.first_name || 'N/A',
        'Last name': each?.last_name || 'N/A',
        Age: each?.age || 'N/A',
        Gender: each.gender ? (each.gender === OTHER_GENDER_OPTION ? each.specific_gender : each.gender) : 'N/A',
        Race: race || 'N/A',
        Income: salary_range_label || 'N/A',
        'Medical Consideration': each?.medical_conditions.join(' ') || 'N/A',
        ...answers,
      }
    })

    yield exportJsonToCsv(participants, fields, action.payload.name, {}, true)
    yield put(showSuccess({ message: 'Survey successfully exported' }))
  } catch (err) {
    console.error(err)
  }
}

function* ExportAllSurvey() {
  try {
    const size = 1000
    const firstRequest: PaginatedList<SurveyFormSchema> = yield call(axios.request, {
      url: `/base/survey_evaluation_result?${queryString.stringify({ page: 0, size })}`,
    })
    const total = firstRequest.count
    let batches = Math.floor(total / size) - 1
    if (batches < 0) batches = 0
    const promises = range(0, batches).map(i =>
      axios.request({
        url: `/base/survey_evaluation_result?${queryString.stringify({ page: i + 1, size })}`,
      }),
    )
    const restResults: PaginatedList<SurveyFormSchema>[] = yield Promise.all(promises)
    const allResults = restResults
      .reduce((acc, el) => {
        return acc.concat(el.data)
      }, firstRequest.data)
      .map(e => formatISOToDate(e, ['start_date', 'end_date'], 'MM/DD/YYYY'))
    yield exportJsonToCsv(
      allResults,
      ['id', 'name', 'age_min', 'age_max', 'genders', 'start_date', 'end_date', 'completions', 'partial_completions'],
      'Survey',
    )
    yield put(showSuccess({ message: 'Surveys successfully exported' }))
  } catch (err) {
    console.error(err)
  }
}

export default function* rootSaga() {
  yield takeLatest(exportAllSurvey.type, ExportAllSurvey)
  yield takeLatest(exportSurvey.type, ExportSurvey)
}
